import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
	ModalHeader, ModalBody, ModalFooter, Button, Spinner, Input, addToast,
	Accordion, AccordionItem
} from '@heroui/react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronDown, Plane, AlertTriangle, CheckCircle } from 'lucide-react';
import CalendarApi from '../../../api/CalendarApi.js';
import DiscrepanciesApi from '../../../api/discrepanciesApi.js';
import { unwrap } from '../utils/apiEnvelope.js';
import { pickCalendarBootstrap, pickCalendarRefs } from '../../calendar-common/viewStoreKeys.js';
import { isFlightFollowingEnabled } from '../../calendar-common/flightFollowing.js';
import { AppDatePicker } from '../../resources/components/AppDatePicker.js';
import { cdtToEpoch, epochToCalendarDateTime } from '../utils/dateTime.js';

// Rem21 Sub 6 — serialize CalendarDate from AppDatePicker → ISO yyyy-MM-dd
// for storage in the custom-field values map. AppDatePicker also accepts a
// raw string back (it parses with `@internationalized/date.parseDate`).
const calendarDateToIso = cdt => {
	if (!cdt || cdt.year == null || cdt.month == null || cdt.day == null) return '';
	return `${cdt.year}-${String(cdt.month).padStart(2, '0')}-${String(cdt.day).padStart(2, '0')}`;
};
import { toCheckInFinishPayload } from '../utils/checkInOutAdapters.js';
import { computeNewCounterValue, cascadeHobbsToResources } from '../utils/resourceCounters.js';
import DiscrepancyView from './DiscrepancyView.js';
import DiscrepancyAddScreen from './DiscrepancyAddScreen.js';
import DiscrepanciesSection from './DiscrepanciesSection.js';
import { getEventOrgPerms } from '../utils/getEventOrgPerms.js';
import { computeDiscrepancyPerms } from '../utils/computeDiscrepancyPerms.js';
import ResourcesApi from '../../../api/resourcesApi.js';

/**
 * CheckInScreen — in-modal screen swap for the Check In flow (Phase 2.1 Sub 6).
 *
 * This is the SHELL + BOOTSTRAP. Subs 7-12 fill in:
 *   - Sub 7: Track Flight readonly summary (departure / flight time / expected
 *     arrival) inherited from check-out; actual arrival required input.
 *   - Sub 8: Meters End table (with `diffCounter`, Hobbs cascade, fixed-2 blur).
 *   - Sub 9: previous-counter auto-fill + out-of-sequence warning.
 *   - Sub 10: custom fields editable.
 *   - Sub 11/12: Discrepancies CRUD (list, add, view, comment, delete, logbook).
 *
 * Replaces the legacy Day-View redirect for `goTo('event-checkin')` —
 * users without Day-View access (e.g. `aartemorlov1998@holdshort.com`
 * Member+Pilot+Instructor+Mechanic) can now complete check-in without
 * leaving Month V2.
 *
 * Bootstrap: `GET /api/v1/org/{org}/calendar/{id}/check-in-start?timezone=…`
 * returns `CheckInStartResponseDto` with `resources[]`, `counters[]`,
 * `customFields[]`, `discrepancies[]`, and `event.additionalData.previousCheckedInEventCounters`.
 * Stashed in local state for downstream subs to consume.
 */
function CheckInScreen({ event, eventId, orgUnique, timezone, eventTypeLabel, onBack, onClose, onSubmitted }) {
	const [loading, setLoading] = useState(true);
	const [criticalError, setCriticalError] = useState(null);
	const [bootstrap, setBootstrap] = useState(null);

	const tz = timezone || 'UTC';

	const fetchBootstrap = useCallback(async () => {
		if (!eventId) return;
		setLoading(true);
		setCriticalError(null);
		try {
			// Rem18 Task 1: cross-org override — pass event's org so multi-org
		// users hit the correct Symfony route, not the URL-path org.
		const res = await CalendarApi.checkInStart(eventId, tz, orgUnique);
			let payload;
			try {
				payload = unwrap(res);
			} catch (e) {
				payload = null;
				// eslint-disable-next-line no-console
				console.warn('[CheckInScreen] check-in-start envelope error:', e);
			}
			setBootstrap(payload || null);
		} catch (err) {
			const msg =
				err?.response?.data?.error?.[0] ||
				err?.message ||
				'Failed to load Check In data';
			setCriticalError(msg);
			addToast({ title: 'Check In load failed', description: msg, color: 'danger' });
		} finally {
			setLoading(false);
		}
	}, [eventId, tz, orgUnique]);

	useEffect(() => {
		fetchBootstrap();
	}, [fetchBootstrap]);

	// ─── Track Flight (Sub 7) ────────────────────────────────────────────────
	// Departure / Flight Time / Expected Arrival come from the check-out
	// record (read-only here); Actual Arrival is editable + REQUIRED on
	// submit when the section is visible. Legacy parity:
	// `calendar.event-checkin.js:44-95, 275-312`.
	// Track Flight gate — view-agnostic bootstrap key (the month key is empty
	// on the DAY page) + boolean-tolerant flag check (Symfony sends `true`,
	// legacy sends '1'; `String(true) === '1'` hid the section EVERYWHERE).
	const enableTrackFlight = useSelector(state =>
		isFlightFollowingEnabled(pickCalendarBootstrap(state)?.organizationSettings)
	);
	const isMaintenance = String(event?.type || '').toLowerCase().includes('maint');
	const showTrackFlight = enableTrackFlight && !isMaintenance;

	const [actualArrivalCdt, setActualArrivalCdt] = useState(null);

	// Derive readonly Track Flight summary values from the bootstrap.
	const trackFlightReadonly = useMemo(() => {
		if (!bootstrap) return null;
		const departureEpoch = Number(bootstrap.departureTime) || null;
		const expectedArrivalEpoch = Number(bootstrap.expectedArrivalTime) || null;
		const flightTimeHours = Number(bootstrap.flightTime);
		let flightTimeText = '—';
		if (Number.isFinite(flightTimeHours) && flightTimeHours > 0) {
			const mins = Math.round(flightTimeHours * 60);
			const hh = Math.floor(mins / 60);
			const mm = mins % 60;
			flightTimeText = `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
		}
		const fmt = epoch => {
			if (!epoch) return '—';
			const cdt = epochToCalendarDateTime(epoch, tz);
			if (!cdt) return '—';
			const dd = String(cdt.day).padStart(2, '0');
			const mo = String(cdt.month).padStart(2, '0');
			const yy = cdt.year;
			const hh = String(cdt.hour ?? 0).padStart(2, '0');
			const mm = String(cdt.minute ?? 0).padStart(2, '0');
			return `${yy}-${mo}-${dd} ${hh}:${mm}`;
		};
		return {
			departureText: fmt(departureEpoch),
			flightTimeText,
			expectedArrivalText: fmt(expectedArrivalEpoch)
		};
	}, [bootstrap, tz]);

	// Initialise Actual Arrival to "now" once the bootstrap arrives. Legacy
	// pre-fills with current time (`calendar.event-checkin.js:44-95`); the
	// user can override before submit. Required field — no submit without
	// a value.
	useEffect(() => {
		if (!showTrackFlight || !bootstrap) return;
		const nowEpoch = Math.floor(Date.now() / 1000);
		setActualArrivalCdt(epochToCalendarDateTime(nowEpoch, tz));
	}, [bootstrap, showTrackFlight, tz]);

	// Submit handler — composes the DTO via `toCheckInFinishPayload` (Phase 2.1
	// Sub 1 adapter). Validates required fields here before POSTing.
	// Subs 8-12 add their per-section state (counters, customFields,
	// discrepancies); this scaffold ensures the actualArrivalTime guard is
	// in place from Sub 7.
	const [submitting, setSubmitting] = useState(false);
	// Sub 8 state declared up here (NOT alongside the rest of Sub 8 below)
	// because `handleSubmit`'s `useCallback` dep list references `meterEnds`.
	// Keeping the `useState` calls AFTER the `useCallback` would hit a
	// Temporal Dead Zone `ReferenceError` on mount. Reviewer flag — fixed
	// via reorder; logic is unchanged.
	const [meterEnds, setMeterEnds] = useState({});          // {[counterId]: newValueText}
	// Phase 2.1 Task:1 (restored) — per custom-resource counter state, keyed by
	// resource id: `{ id, name, type, counterValue, diffCounter, newCounterValue }`.
	// `counterValue` is the LIVE base reading from check-in-start (NOT a
	// snapshot); `newCounterValue = counterValue + diffCounter` (2-dp). Restores
	// the legacy resource-counter table (`calendar.event-checkin.js:415-446`)
	// that Rem21 Sub 11 wrongly removed. Declared up here (like `meterEnds`) so
	// `handleSubmit`'s useCallback dep list can reference it without a TDZ error.
	const [resourceCounters, setResourceCounters] = useState({}); // {[resId]: {...}}
	// Org meter-source settings (legacy `Calendar.orgSettings.{instructorMeter,
	// resourceMeter}`) — drive the Hobbs cascade target selection. Primitives so
	// the cascade callback's deps stay stable.
	const instructorMeter = useSelector(
		state => pickCalendarBootstrap(state)?.organizationSettings?.instructorMeter || ''
	);
	const resourceMeter = useSelector(
		state => pickCalendarBootstrap(state)?.organizationSettings?.resourceMeter || ''
	);

	// ─── Custom fields + comment (Sub 10) ────────────────────────────────────
	// `customFieldValues` keyed by typeId; the adapter (`toCheckInFinishPayload`)
	// renames this map to the `customFields[]` DTO shape and drops empties.
	// `commentText` maps to DTO field `comment` (SINGULAR — see Sub 1).
	const [customFieldValues, setCustomFieldValues] = useState({}); // {[typeId]: value}
	const [commentText, setCommentText] = useState('');
	// Rem22 Sub 7 — per-field inline validation flags. Set on submit when a
	// required field is blank; cleared when the user starts typing in that
	// field. Drives the red-border + helper-text rendering in the Event
	// Fields renderer. Layered on top of the existing toast for richer UX.
	const [customFieldErrors, setCustomFieldErrors] = useState({}); // {[typeId]: true}

	// Sub 11b — Org-level custom field DEFINITIONS (sourced from
	// `state.calendarMonthRefs.customFields` via the per-org
	// `listCustomFields()` fan-out at `refsSlice.js:91`). Distinct from
	// `bootstrap.customFields` which only carries per-event SET VALUES.
	// Hoisted (Rem21 Sub 2) so `handleSubmit` can validate against the
	// merged list without TDZ.
	const orgCustomFieldDefs = useSelector(state => {
		const list = pickCalendarRefs(state)?.customFields;
		return Array.isArray(list) ? list : [];
	});

	// Merged list: every org-level definition appears once, with any
	// bootstrap value pre-populated by typeId match. Falls back to the raw
	// bootstrap array when org-level definitions aren't loaded yet (defensive
	// — refs slice is fan-out, may still be `idle`/`pending` on first paint).
	const mergedCustomFields = useMemo(() => {
		const bsList = Array.isArray(bootstrap?.customFields) ? bootstrap.customFields : [];
		if (orgCustomFieldDefs.length === 0) return bsList;
		const bsByTypeId = new Map();
		bsList.forEach(cf => {
			const tid = cf?.typeId ?? cf?.id;
			if (tid != null) bsByTypeId.set(String(tid), cf);
		});
		return orgCustomFieldDefs.map(def => {
			const tid = def?.typeId ?? def?.id;
			const bsMatch = tid != null ? bsByTypeId.get(String(tid)) : null;
			return {
				typeId: tid,
				name: def?.name ?? def?.label ?? bsMatch?.name ?? bsMatch?.label,
				type: def?.type ?? bsMatch?.type ?? 'text',
				required: def?.required ?? bsMatch?.required ?? false,
				value: bsMatch?.value ?? null
			};
		});
	}, [orgCustomFieldDefs, bootstrap?.customFields]);

	// ─── Previous-counter auto-fill + out-of-sequence (Sub 9) ───────────────
	// Hoisted (same TDZ pattern as `meterEnds`/`mergedCustomFields`) so the
	// `metersRows`/`previousHint` memo below can reference `previousByCounterId`
	// without a ReferenceError on mount. (Rem38 M8 dropped its use from the
	// `handleSubmit` gate AND the End auto-prefill effect — both now read the
	// live `c.value`; the snapshot survives only as the read-only `previousHint`
	// label.) Legacy parity: `calendar.event-checkin.js:105-121, 242, 361-367`.
	const previousEventCounters = useMemo(() => {
		const a = event?.additionalData?.previousCheckedInEventCounters;
		if (a !== undefined && a !== null) return Array.isArray(a) ? a : null;
		const b = event?.previousCheckedInEventCounters;
		if (b !== undefined && b !== null) return Array.isArray(b) ? b : null;
		return null;
	}, [event?.additionalData?.previousCheckedInEventCounters, event?.previousCheckedInEventCounters]);

	const isOutOfSequence = Array.isArray(previousEventCounters) && previousEventCounters.length > 0;

	const previousByCounterId = useMemo(() => {
		const map = {};
		(previousEventCounters || []).forEach(p => {
			if (p?.id == null) return;
			if (p.value == null || p.value === '') return;
			map[String(p.id)] = String(p.value);
		});
		return map;
	}, [previousEventCounters]);

	const handleSubmit = useCallback(async () => {
		if (!eventId) return;
		// Sub 7 validation: Track Flight section requires Actual Arrival.
		let actualArrivalEpoch = null;
		if (showTrackFlight) {
			actualArrivalEpoch = cdtToEpoch(actualArrivalCdt, tz);
			if (!actualArrivalEpoch) {
				addToast({
					title: 'Actual arrival is required',
					description: 'Please enter the actual arrival time.',
					color: 'warning'
				});
				return;
			}
		}

		// Sub 10 validation: required custom fields must be non-empty.
		// Backend `CheckInFinishRequestDto` rejects required fields with
		// blank `value` — block submit client-side with a clear message.
		// Rem21 Sub 2 — validate against `mergedCustomFields` (the
		// renderer's source) NOT `bootstrap.customFields` (which only
		// carries event-level values). An org-level required field with
		// no existing value renders as a blank input (Rem20 Sub 11b) and
		// must be caught here before submit.
		// Rem22 Sub 7 — collect ALL missing required fields (not just the
		// first), so the user sees every offending input flagged at once.
		const customFieldsList = mergedCustomFields;
		const missingMap = {};
		customFieldsList.forEach(cf => {
			if (!cf?.required && cf?.required !== '1' && cf?.required !== 1) return;
			const tid = cf?.typeId ?? cf?.id;
			if (tid == null) return;
			const v = customFieldValues[String(tid)];
			const blank = v == null || (typeof v === 'string' && v.trim() === '');
			if (blank) missingMap[String(tid)] = true;
		});
		if (Object.keys(missingMap).length > 0) {
			setCustomFieldErrors(missingMap);
			const first = customFieldsList.find(cf => missingMap[String(cf?.typeId ?? cf?.id)]);
			addToast({
				title: 'Required field missing',
				description: `Please fill in "${first?.name || first?.label || 'custom field'}".`,
				color: 'warning'
			});
			return;
		}
		// All required fields satisfied — clear any prior errors.
		if (Object.keys(customFieldErrors).length > 0) setCustomFieldErrors({});

		// Sub 8 hard gate: End < Start blocks submit. Validation source MUST
		// match the UI Start display. Rem38 M8: the UI Start now always shows
		// the LIVE `c.value` (= AircraftCounter.value, reflecting any Pencil
		// edits at CheckOut) for out-of-sequence events too — so the gate reads
		// `c.value` as Start, NOT the `previousByCounterId` snapshot. Reading the
		// snapshot here would diverge from the displayed Start and could fire a
		// false-positive (or miss a real negative diff). Toast is `danger` and we
		// early-return to prevent submit.
		const reverseRow = (Array.isArray(bootstrap?.counters) ? bootstrap.counters : [])
			.map(c => {
				const start = Number(c?.value);
				const end = Number(meterEnds[String(c?.id)] ?? '');
				if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
				if (end < start) return c?.name || `Counter #${c?.id}`;
				return null;
			})
			.find(Boolean);
		if (reverseRow) {
			addToast({
				title: 'Counter went down',
				description: `End value lower than Start for ${reverseRow}.`,
				color: 'danger'
			});
			return;
		}

		// Build the final DTO via the Phase 2.1 Sub 1 adapter. The adapter
		// renames `comment` (singular) per `CalendarCheckInManager.php:141-143`
		// and drops empty `customFields` entries. Counters/resources arrays
		// are already DTO-shaped by Sub 8's `countersSubmitFragment`; the
		// adapter's shapers normalize idempotently.
		const counters = Array.isArray(bootstrap?.counters) ? bootstrap.counters : [];
		const countersPayload = counters.map(c => {
			const id = c?.id;
			const endRaw = meterEnds[String(id)] ?? '';
			const endNum = endRaw === '' ? null : Number(endRaw);
			return {
				id,
				value: c?.value,
				newValue: Number.isFinite(endNum) ? endNum : null
			};
		});
		// Phase 2.1 Task:1 (restored) — emit per custom-resource counters.
		// `shapeResources` (`checkInOutAdapters.js:84-92`) maps `{id, newCounterValue}`
		// → DTO `resources[]`. Aircraft/simulator are NOT here (excluded at seed),
		// so they're only counted once via `counters[]` (no double-apply in
		// `CalendarCheckInManager::setResourcesCounters`).
		const resourcesPayload = Object.values(resourceCounters)
			.filter(r => r && r.id != null)
			.map(r => ({ id: r.id, newCounterValue: r.newCounterValue }));

		const formForAdapter = {
			actualArrivalTime: actualArrivalEpoch ?? undefined,
			comment: commentText,
			customValues: customFieldValues,
			counters: countersPayload,
			resources: resourcesPayload
		};
		const payload = toCheckInFinishPayload(formForAdapter);

		setSubmitting(true);
		try {
			// Rem18 Task 1: cross-org override.
			const res = await CalendarApi.checkInFinish(eventId, payload, tz, orgUnique);
			const body = res?.data ?? res;
			if (body?.success === false) {
				const msg = (body.error && body.error[0]) || 'Check In failed';
				addToast({ title: 'Check In failed', description: msg, color: 'danger' });
				return;
			}
			addToast({ title: 'Checked in', color: 'success' });
			onSubmitted?.();
		} catch (err) {
			const msg = err?.response?.data?.error?.[0] || err?.message || 'Check In failed';
			addToast({ title: 'Check In failed', description: msg, color: 'danger' });
		} finally {
			setSubmitting(false);
		}
	}, [
		eventId,
		showTrackFlight,
		actualArrivalCdt,
		tz,
		onSubmitted,
		orgUnique,
		bootstrap,
		meterEnds,
		resourceCounters,
		customFieldValues,
		commentText,
		mergedCustomFields,
		customFieldErrors
	]);

	// Payload contribution memo — Sub 12 will spread this into the adapter
	// call. Returns `{}` when Track Flight is hidden so spreading is a no-op.
	// eslint-disable-next-line no-unused-vars
	const trackFlightSubmitFragment = useMemo(() => {
		if (!showTrackFlight) return {};
		const epoch = cdtToEpoch(actualArrivalCdt, tz);
		return epoch ? { actualArrivalTime: epoch } : {};
	}, [showTrackFlight, actualArrivalCdt, tz]);

	// ─── Meters End + Custom resource counters (Sub 8) ──────────────────────
	// Legacy parity: `calendar.event-checkin.js:127-173, 384-448`.
	//
	// `counters` table: one row per shared meter (Hobbs / Engine X). Each
	// row has Start (OLD value, readonly) + End (editable) + Total
	// (computed). On blur, End is formatted to 2 decimals. OLD value is
	// preserved in the submit payload (`value` field) so backend's
	// out-of-sequence math at `CalendarCheckInManager.php:299-331` still
	// computes correctly.
	//
	// `resources` table: per custom-resource counter overrides. When Hobbs End
	// changes, cascade to resources whose org-configured meter is `hobbs`
	// (instructor resources → `instructorMeter`, others → `resourceMeter`):
	// `diff = HobbsEnd − HobbsLiveStart` (if ≥0), `newCounterValue = counterValue + diff`.
	// `meterEnds` + `resourceCounters` state moved above `handleSubmit` to
	// avoid Temporal Dead Zone on the useCallback dep list (Reviewer fix).
	// The init effect, helpers, and memos that consume them stay here.

	// Sub 9 — `previousEventCounters`, `isOutOfSequence`, `previousByCounterId`
	// hoisted above `handleSubmit` for TDZ-safety. Legacy parity copy:
	//   "The updated meter values will not affect inspection tracking"
	// See the hoisted block just before `handleSubmit`.

	useEffect(() => {
		if (!bootstrap) return;
		// Pre-fill End meter values. Rem38 M8 follow-up: End defaults to the
		// LIVE `c.value` (same source-of-truth as the Start column), NOT the
		// `previousByCounterId` snapshot. The snapshot fallback used to make an
		// out-of-sequence event open with End = previous reading while Start =
		// live value → an immediate NEGATIVE Total (red ⚠️) before any user
		// input. Aligning End-prefill with Start gives Total = 0.00 at open.
		// Sequential events are unchanged (snapshot map empty → `c.value`
		// already). The previous reading is still surfaced as the read-only
		// `previousHint` label under the input. `previousByCounterId` is no
		// longer referenced here (still used by `metersRows` for the hint).
		const counterMap = {};
		(Array.isArray(bootstrap.counters) ? bootstrap.counters : []).forEach(c => {
			if (c?.id == null) return;
			const id = String(c.id);
			let newVal = c.newValue;
			if (newVal == null || newVal === '') {
				newVal = c.value ?? '';
			}
			counterMap[id] = newVal === null || newVal === undefined ? '' : String(newVal);
		});
		setMeterEnds(counterMap);

		// Phase 2.1 Task:1 (restored) — seed per custom-resource counters from
		// the check-in-start `resources[]` payload. Backend pre-fills each with
		// `counterValue` (live base reading), `diffCounter` (= event length) and
		// `newCounterValue` (= counterValue + length). Exclude aircraft/simulator
		// — those are handled by the Meters table above (legacy `customResources`
		// filter, `calendar.selectors.js:882-896`). diff/new kept as 2-dp strings
		// to match the editable input + legacy formatting.
		const resMap = {};
		(Array.isArray(bootstrap.resources) ? bootstrap.resources : []).forEach(r => {
			if (r?.id == null) return;
			if (r.type === 'aircraft' || r.type === 'simulator') return;
			const counterValue = Number(r.counterValue);
			const base = Number.isFinite(counterValue) ? counterValue : 0;
			const diffNum = Number(r.diffCounter);
			const diff = Number.isFinite(diffNum) ? diffNum : 0;
			const newNum = Number(r.newCounterValue);
			const newVal = Number.isFinite(newNum) ? newNum : base + diff;
			resMap[String(r.id)] = {
				id: r.id,
				name: r.name ?? '',
				type: r.type ?? '',
				counterValue: base,
				diffCounter: diff.toFixed(2),
				newCounterValue: newVal.toFixed(2)
			};
		});
		setResourceCounters(resMap);

		// Sub 10 init: pre-fill custom field inputs from bootstrap, and the
		// comment textarea from the event's existing comments. Both surfaces
		// are session-scoped — re-fetching the bootstrap resets the state.
		const cfMap = {};
		(Array.isArray(bootstrap.customFields) ? bootstrap.customFields : []).forEach(cf => {
			const typeId = cf?.typeId ?? cf?.id;
			if (typeId == null) return;
			const v = cf?.value ?? '';
			cfMap[String(typeId)] = v == null ? '' : String(v);
		});
		setCustomFieldValues(cfMap);
		setCommentText(String(event?.comments ?? event?.description ?? ''));
	}, [bootstrap, event?.comments, event?.description]);

	const findHobbsCounter = useCallback(() => {
		const counters = Array.isArray(bootstrap?.counters) ? bootstrap.counters : [];
		// Prefer an explicit flag (`isHobbs`) if backend sets one; otherwise
		// match on the canonical "Hobbs" label.
		return (
			counters.find(c => c?.isHobbs === true || c?.isHobbs === '1') ||
			counters.find(c => String(c?.name || '').trim().toLowerCase() === 'hobbs') ||
			null
		);
	}, [bootstrap]);

	const handleMeterEndChange = useCallback((counterId, text) => {
		// Rem38 M1 — normalize a UA-locale decimal comma to a dot so `Number()`
		// parses correctly (`Number("61,50")` is NaN) and the separator stays
		// consistent with the dot-formatted Start column. The input is now
		// `type="text"` + `inputMode="decimal"`, so the browser no longer
		// localizes the value for us.
		const normalized = typeof text === 'string' ? text.replace(',', '.') : text;
		setMeterEnds(prev => ({ ...prev, [String(counterId)]: normalized }));

		// Phase 2.1 Task:1 (restored) — Hobbs cascade. When the Hobbs meter End
		// changes, recompute the diff/new value for every custom resource whose
		// configured meter is `hobbs` (instructor resources gated on the org
		// `instructorMeter`, others on `resourceMeter`). Legacy parity:
		// `calendar.event-checkin.js:174-205`. Live model: Hobbs START is the
		// meter's LIVE `c.value` (NOT a snapshot); only cascade when End ≥ Start.
		const hobbs = findHobbsCounter();
		if (!hobbs || String(hobbs.id) !== String(counterId)) return;
		setResourceCounters(prev =>
			cascadeHobbsToResources(prev, {
				hobbsStart: hobbs.value,
				hobbsEnd: normalized,
				instructorMeter,
				resourceMeter
			})
		);
	}, [findHobbsCounter, instructorMeter, resourceMeter]);

	// Phase 2.1 Task:1 (restored) — per custom-resource diff input handlers.
	// `newCounterValue = counterValue + diffCounter` (2-dp); blur formats the
	// diff to 2 decimals. Legacy parity: `handleCustomMetersChange` / `toFixed`
	// (`calendar.event-checkin.js:146-172`).
	const handleResourceDiffChange = useCallback((resId, text) => {
		const normalized = typeof text === 'string' ? text.replace(',', '.') : text;
		setResourceCounters(prev => {
			const r = prev[String(resId)];
			if (!r) return prev;
			const newVal = computeNewCounterValue(r.counterValue, normalized, r.newCounterValue);
			return { ...prev, [String(resId)]: { ...r, diffCounter: normalized, newCounterValue: newVal } };
		});
	}, []);

	const handleResourceDiffBlur = useCallback(resId => {
		setResourceCounters(prev => {
			const r = prev[String(resId)];
			if (!r) return prev;
			const n = Number(r.diffCounter);
			if (!Number.isFinite(n)) return prev;
			const newVal = computeNewCounterValue(r.counterValue, n, r.newCounterValue);
			return { ...prev, [String(resId)]: { ...r, diffCounter: n.toFixed(2), newCounterValue: newVal } };
		});
	}, []);

	const handleMeterEndBlur = useCallback(counterId => {
		setMeterEnds(prev => {
			const raw = prev[String(counterId)];
			if (raw == null || raw === '') return prev;
			const n = Number(raw);
			if (!Number.isFinite(n)) return prev;
			return { ...prev, [String(counterId)]: n.toFixed(2) };
		});
	}, []);

	// Phase 2.1 Task:1 (restored) — resource diff handlers live above (next to
	// the Hobbs cascade in `handleMeterEndChange`).

	// Derive per-counter view + payload shape.
	const metersRows = useMemo(() => {
		const counters = Array.isArray(bootstrap?.counters) ? bootstrap.counters : [];
		return counters.map(c => {
			const id = String(c?.id);
			// Rem38 M8 — DIVERGENCE from the original Phase 2.1 Sub 9 design.
			// Sub 9 used the previous event's reading (`previousByCounterId`) as
			// the out-of-sequence Start. That snapshot HIDES a Pencil edit the
			// user made to the aircraft counter at CheckOut: the edit lands in
			// `AircraftCounter.value` → `check-in-start` returns the fresh
			// `c.value`, but the snapshot (from the previous event's check-in)
			// would win and show the stale number. So the LIVE `c.value` now
			// always wins for the Start column. `oldValue` (sent on the wire)
			// still tracks `c.value` so backend's out-of-sequence math at
			// `CalendarCheckInManager.php:299-331` keeps its baseline, and the
			// previous reading is surfaced only as the `previousHint` UX label.
			// DO NOT revert to `prevStr ?? c.value` — that re-introduces the bug.
			const prevStr = previousByCounterId[id];
			const startSource = c?.value;
			const startNum = Number(startSource);
			const endText = meterEnds[id] ?? '';
			const endNum = Number(endText);
			const totalNum = Number.isFinite(endNum) && Number.isFinite(startNum)
				? endNum - startNum
				: null;
			return {
				id: c?.id,
				name: c?.name || c?.label || `Counter #${c?.id}`,
				// Rem38 M2 — always render 2 decimals so an integer-valued
				// counter (Hobbs sends `72`) shows `72.00`, matching the float
				// counters (`61.39`). Display-only string; numeric math uses
				// `startNum` above.
				start: Number.isFinite(startNum) ? startNum.toFixed(2) : (startSource ?? '—'),
				endText,
				endNum: Number.isFinite(endNum) ? endNum : null,
				total: totalNum,
				oldValue: c?.value,                   // unchanged — for the wire payload
				previousHint: prevStr || null          // for the "Previous: X" UI hint
			};
		});
	}, [bootstrap, meterEnds, previousByCounterId]);

	// `countersSubmitFragment` emits the meter `counters[]` shape. Resources are
	// built separately in `handleSubmit` from `resourceCounters` and rendered via
	// the `resourceRows` memo below. This memo stays unused (only `handleSubmit`
	// builds the payload directly), kept stable for any future consumer.
	// eslint-disable-next-line no-unused-vars
	const countersSubmitFragment = useMemo(() => {
		const counters = metersRows
			.filter(r => r.id != null)
			.map(r => ({
				id: r.id,
				value: r.oldValue,                                      // OLD reading (out-of-sequence math)
				newValue: r.endText === '' ? null : Number(r.endText)   // user-entered End
			}));
		return { counters };
	}, [metersRows]);

	// Phase 2.1 Task:1 (restored) — ordered view rows for the custom resource
	// counter table (preserves the bootstrap `resources[]` order; reads the live
	// diff/new value from `resourceCounters`). Aircraft/simulator already
	// excluded at seed time.
	const resourceRows = useMemo(() => {
		const list = Array.isArray(bootstrap?.resources) ? bootstrap.resources : [];
		return list
			.filter(r => r?.id != null && r.type !== 'aircraft' && r.type !== 'simulator')
			.map(r => {
				const rc = resourceCounters[String(r.id)] || {};
				return {
					id: r.id,
					name: r.name ?? '',
					type: r.type ?? '',
					diffCounter: rc.diffCounter ?? '',
					newCounterValue: rc.newCounterValue ?? ''
				};
			});
	}, [bootstrap, resourceCounters]);

	// ─── Discrepancies inline list (Sub 11) + Add/View screens (Sub 12) ──────
	// Reads `bootstrap.discrepancies[]` (`CheckInStartResponseDto.discrepancies`).
	// Reuse the existing `DiscrepanciesApi` wrappers — Symfony singular routes
	// `/discrepancy/...`. Sub 12 mounts `DiscrepancyAddScreen` / `DiscrepancyView`
	// via an internal screen-swap so the user can drill into a discrepancy
	// without unwinding the Check In session.
	const [localDiscrepancies, setLocalDiscrepancies] = useState([]);
	const [internalScreen, setInternalScreen] = useState('main');     // 'main' | 'discrepancy-add' | 'discrepancy-view'
	const [selectedDiscrepancy, setSelectedDiscrepancy] = useState(null);

	// Aircraft id for Add / list refresh — same picker pattern as
	// CheckOutScreen. Maintenance / simulator events return `null`.
	// Sub 11a: return the FULL aircraft entity (not just the id) so the
	// Aircraft header section can read `registrationNumber` /
	// `medicalCertRequired` / `endorsements` etc directly.
	const aircraftEntity = useMemo(() => {
		const resources = Array.isArray(event?.eventResources) ? event.eventResources : [];
		for (const er of resources) {
			const aircraft =
				er?.aircraft ||
				er?.resource?.aircraft ||
				(er?.resource && String(er.resource?.type) === 'aircraft' ? er.resource : null);
			if (aircraft?.id != null) return aircraft;
		}
		return null;
	}, [event?.eventResources]);
	const aircraftId = aircraftEntity?.id ?? null;

	// Sub 11a — Aircraft header is a HeroUI Accordion (Rem22 Sub 10).
	// Default collapsed for legacy parity (`calendar.event-checkin.js` aircraft
	// card). The Accordion component owns its own open state internally —
	// dropped the prior `showAircraftDetails` useState.

	// Rem21 Sub 8 — fetch full aircraft DTO via the dedicated endpoint so
	// the Show Details panel renders fields that aren't serialized in
	// `Event::EVENT_READ_GROUP` (registrationNumber / pilotRating / instructorRating
	// / endorsements / medicalCertificate). Same pattern as Rem20 Sub 12
	// (inspections via dedicated endpoint). Failure → fall back to the
	// minimal aircraft entity from the event detail.
	const [aircraftDetails, setAircraftDetails] = useState(null);
	useEffect(() => {
		if (aircraftId == null) {
			setAircraftDetails(null);
			return;
		}
		let cancelled = false;
		ResourcesApi.getAircraft(aircraftId, orgUnique)
			.then(res => {
				if (cancelled) return;
				const payload = res?.data?.data ?? res?.data ?? null;
				setAircraftDetails(payload && typeof payload === 'object' ? payload : null);
			})
			.catch(err => {
				if (cancelled) return;
				// eslint-disable-next-line no-console
				console.warn('[CheckInScreen] getAircraft failed:', err?.response?.status || err?.message);
				setAircraftDetails(null);
			});
		return () => { cancelled = true; };
	}, [aircraftId, orgUnique]);

	// Effective aircraft for the header — prefer the dedicated-endpoint DTO
	// when available, otherwise fall back to the embedded one from event detail.
	const aircraftForHeader = aircraftDetails || aircraftEntity;

	// Mechanics list for Add's assign-to picker. Sourced from the refs
	// slice's fan-out (Phase 2 Rem16 Sub 7's `Promise.allSettled` keeps this
	// populated even when other refs fail).
	const mechanics = useSelector(state => {
		const list = pickCalendarRefs(state)?.mechanics;
		return Array.isArray(list) ? list : [];
	});

	// Rem21 Sub 2 — `orgCustomFieldDefs` + `mergedCustomFields` HOISTED to
	// just before `handleSubmit` at the top of the component (TDZ-safe for
	// the validation closure). See block above the state hooks.

	// User id + settings — DiscrepancyView consumes both for "It's you"
	// chips and date-format preferences.
	const currentUserId = useSelector(state =>
		state?.user?.id ?? state?.user?.userId ?? state?.user?._userID ?? null
	);
	const userSettings = useSelector(state => pickCalendarBootstrap(state)?.userSettings);

	// Wrapped per-org perms used by `DiscrepancyView`'s gating (it reads
	// `canManageDiscrepancies / canChangeDiscrepancyStatus /
	// canCommentDiscrepancy / canDeleteDiscrepancy` from the wrapped shape
	// produced by `buildCalendarPermissionsForOrg`). My raw `discrepancyPerms`
	// (4 booleans above) is still used for the inline list — both coexist
	// because they serve different consumers.
	const viewPerms = useSelector(state =>
		getEventOrgPerms(state, event?.organizationUnique || event?.orgUnique || orgUnique)
	);

	// Discrepancy permissions — Rem21 Sub 1 (Reviewer rev 1): selector
	// extracted to `computeDiscrepancyPerms` pure utility for testability +
	// reuse. Field names corrected against the live 41-item catalog
	// (`discrepanciesAdd` / `discrepanciesManage` / `discrepanciesStatus` /
	// `discrepanciesView`); `canAssign` / `canClose` removed as unused.
	const discrepancyPerms = useSelector(state =>
		computeDiscrepancyPerms(
			pickCalendarBootstrap(state),
			event?.organizationUnique || event?.orgUnique || null
		)
	);

	// Refresh helper — re-fetches the aircraft's discrepancies via the
	// existing API wrapper so Add / Delete / Status edits propagate back to
	// the list without re-bootstrapping the whole Check In screen.
	// Gated on `discrepanciesView`: without it the endpoint answers 403 on every
	// open and after every save, and the list isn't shown anyway.
	const refreshDiscrepancies = useCallback(async () => {
		if (!aircraftId || !discrepancyPerms.canView) return;
		try {
			const res = await DiscrepanciesApi.getAircraftDiscrepancies(aircraftId, orgUnique);
			const payload = Array.isArray(unwrap(res)) ? unwrap(res) : [];
			setLocalDiscrepancies(payload);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.warn('[CheckInScreen] discrepancies refresh failed:', err);
		}
	}, [aircraftId, orgUnique, discrepancyPerms.canView]);

	// NOTE: the prior `useEffect [bootstrap]` that seeded `localDiscrepancies`
	// from `bootstrap.discrepancies` was REMOVED (epic e5cea696). `CheckInStartResponseDto`
	// does NOT carry a `discrepancies` field, so `Array.isArray(undefined) === false`
	// clobbered the canonical data fetched by `refreshDiscrepancies` below with `[]`.
	// `getAircraftDiscrepancies` (effect below) is now the single source of truth.

	// Rem22 Sub 3 — fetch discrepancies on mount independent of `bootstrap`.
	// When `check-in-start` rejects (e.g. past-event-not-yet-checked-out 400),
	// `bootstrap` stays null → the effect above never seeds the list.
	// `refreshDiscrepancies` already calls `DiscrepanciesApi.getAircraftDiscrepancies`
	// which works regardless of check-in-start state. Keyed on `aircraftId` so
	// it re-fires when the user switches between aircraft events.
	useEffect(() => {
		if (aircraftId == null) return;
		refreshDiscrepancies();
	}, [aircraftId, refreshDiscrepancies]);

	// Discrepancy filtering, sorting, inline status/priority/assignTo editing,
	// and display resolvers all live in the shared `DiscrepanciesSection`
	// component (epic e5cea696) — CheckIn renders it below with raw boolean
	// perms and `refreshDiscrepancies` as the post-save callback.

	const handleAddDiscrepancyClick = useCallback(() => {
		if (!aircraftId) {
			addToast({
				title: 'No aircraft',
				description: 'This event has no aircraft to attach a discrepancy to.',
				color: 'warning'
			});
			return;
		}
		setInternalScreen('discrepancy-add');
	}, [aircraftId]);

	const handleDiscrepancyRowClick = useCallback(discrepancy => {
		setSelectedDiscrepancy(discrepancy);
		setInternalScreen('discrepancy-view');
	}, []);

	const handleDiscrepancyBack = useCallback(async (didChange) => {
		setSelectedDiscrepancy(null);
		setInternalScreen('main');
		if (didChange) await refreshDiscrepancies();
	}, [refreshDiscrepancies]);

	// Sub 12 — internal screen swap. When the user drills into a discrepancy,
	// render the dedicated screen instead of the main check-in body. Keeps
	// the rest of the Check In session intact (state preserved across the
	// nested navigation).
	if (internalScreen === 'discrepancy-add') {
		return (
			<DiscrepancyAddScreen
				aircraftId={aircraftId}
				orgUnique={orgUnique}
				mechanics={mechanics}
				onBack={handleDiscrepancyBack}
			/>
		);
	}
	if (internalScreen === 'discrepancy-view' && selectedDiscrepancy) {
		return (
			<DiscrepancyView
				discrepancy={selectedDiscrepancy}
				orgUnique={orgUnique}
				currentUserId={currentUserId}
				perms={viewPerms}
				onBack={() => handleDiscrepancyBack(true)}
				onBackToEvent={() => handleDiscrepancyBack(true)}
				onDelete={() => handleDiscrepancyBack(true)}
				userSettings={userSettings}
				eventTypeLabel={eventTypeLabel}
				resourceLabel={null}
			/>
		);
	}

	return (
		<>
			<ModalHeader className="flex flex-col border-b border-default-200 pr-10">
				<div className="flex items-center gap-2 text-xs text-default-500 mb-1">
					<span>{eventTypeLabel || 'Regular Event'}</span>
					<span>/</span>
					<span>Check In</span>
				</div>
				<div className="flex items-center gap-3">
					<Button isIconOnly size="sm" variant="light" onPress={onBack} aria-label="Back to event">
						<ChevronLeft className="w-4 h-4" />
					</Button>
					<span style={{ color: 'hsl(var(--heroui-primary))' }}>
						<Plane className="w-5 h-5" />
					</span>
					<span className="text-lg font-bold flex-1" style={{ color: 'hsl(var(--heroui-foreground))' }}>
						Check In
					</span>
				</div>
			</ModalHeader>

			<ModalBody className="pt-4">
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
				>
					{loading && (
						<div className="flex items-center justify-center py-12 gap-3">
							<Spinner size="sm" />
							<span className="text-sm text-default-500">Loading Check In…</span>
						</div>
					)}
					{/* Rem23 Sub 5 — when `check-in-start` fails (criticalError set),
					    Aircraft header + Discrepancies still render — they are
					    derived from the event detail + dedicated discrepancy endpoint,
					    not from `bootstrap`. The Meters / Event Fields / Submit
					    sections (which DO depend on bootstrap) stay hidden behind
					    the inner `!criticalError` gates below. */}
					{!loading && criticalError && (
						<div className="border border-danger-200 bg-danger-50 text-danger-700 rounded-md p-3 text-sm mb-3">
							{criticalError}
						</div>
					)}
					{!loading && (
						<div className="text-sm text-default-600 space-y-4">
							{/*
								Subs 8-12 fill in:
								  Sub 8  → Meters End editable table
								  Sub 9  → previous-counter auto-fill + out-of-sequence
								  Sub 10 → custom fields editable
								  Sub 11 → Discrepancies CRUD
								The bootstrap state (`bootstrap`) is stashed and ready
								to consume.
							*/}
							{/* Rem23 Sub 5 — only show the empty-state when bootstrap
							    fetch returned EMPTY (no error). When criticalError is
							    set, the danger banner above explains the failure
							    already; don't double up. */}
							{!criticalError && bootstrap == null && (
								<p className="text-default-400 italic">No check-in data available.</p>
							)}

							{/* Out-of-sequence warning panel (Sub 9) — legacy parity
							    `calendar.event-checkin.js:322-330`. Appears when
							    `event.additionalData.previousCheckedInEventCounters`
							    is non-null (a later event was already checked in).
							    Exact legacy copy preserved. */}
							{isOutOfSequence && (
								<div
									className="border border-warning-200 bg-warning-50 text-warning-700 rounded-md p-3 flex gap-2"
									role="alert"
								>
									<AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
									<p className="text-xs">
										The updated meter values will not affect inspection tracking
									</p>
								</div>
							)}

							{/* Sub 11a / Rem22 Sub 10 — Aircraft header as a HeroUI
							    Accordion (replaces the prior custom toggle + manual
							    `showAircraftDetails` state). Legacy parity:
							    `calendar.event-checkin.js` aircraft card. Default
							    collapsed. Title carries the airworthy ✓ (Sub 8). */}
							{aircraftEntity && (
								<section className="border border-default-200 rounded-md overflow-hidden">
								{/* Rem24 Sub 5 — Accordion switched to `variant="light"`
								    (no built-in chrome) wrapped in the canonical METERS /
								    EVENT FIELDS section border (`border border-default-200
								    rounded-md`) to defeat the prior double-border look. */}
								<Accordion
									variant="light"
									className="px-0"
									isCompact
								>
									<AccordionItem
										key="aircraft-details"
										aria-label="Aircraft details"
										/* Rem38 M3 — the HeroUI default indicator is a
										   ChevronLeft that rotates to point DOWN on open, so
										   it reads as pointing LEFT (`<`) when collapsed.
										   `disableIndicatorAnimation` turns off that built-in
										   rotation; the custom indicator below renders a
										   ChevronDown (points DOWN when closed) and rotates
										   180° to point UP when open. */
										disableIndicatorAnimation
										indicator={({ isOpen }) => (
											<ChevronDown
												className={`w-4 h-4 text-default-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
											/>
										)}
										classNames={{
											/* Rem22 Sub 13 — match METERS / EVENT FIELDS
											   section visual rhythm (px-3 py-2 + softer
											   border + no shadow). */
											base: 'shadow-none',
											trigger: 'px-3 py-2',
											content: 'px-3 py-2 pt-0',
											title: 'text-sm font-medium'
										}}
										title={
											<span className="text-sm font-medium text-default-800 flex items-center gap-1">
												<span className="text-default-500 mr-1">Aircraft:</span>
												{aircraftForHeader.registrationNumber || aircraftForHeader.regNumb || aircraftForHeader.name || `Aircraft #${aircraftForHeader.id}`}
												{/* Rem22 Sub 8 — `inspectionStatus` fallback chain.
												    Rem22 Sub 12 — unified canonical airworthy icon
												    (`CheckCircle` from lucide-react with primary
												    project styling) matching EventViewModal
												    Resources subpanel render at
												    `EventViewModal.js:1384`. */}
												{String(aircraftEntity?.inspectionStatus ?? aircraftDetails?.inspectionStatus ?? '').toLowerCase() === 'airworthy' && (
													<CheckCircle
														className="w-4 h-4 text-success flex-shrink-0"
														fill="currentColor"
														stroke="white"
														strokeWidth={2.5}
														aria-label="Airworthy"
													/>
												)}
											</span>
										}
									>
										<ul className="divide-y divide-default-100">
											{[
												{ label: 'Registration Number', value: aircraftForHeader.registrationNumber || aircraftForHeader.regNumb || aircraftForHeader.name },
												{ label: 'Medical Certificate Required', value: aircraftForHeader.medicalCertificate?.name ?? aircraftForHeader.medicalCertificate ?? aircraftForHeader.medicalCertRequired ?? aircraftForHeader.medicalGrade },
												{ label: 'Endorsements', value: typeof aircraftForHeader.endorsements === 'object' && aircraftForHeader.endorsements != null
													? Object.entries(aircraftForHeader.endorsements).filter(([, v]) => v).map(([k]) => k).join(', ') || '—'
													: aircraftForHeader.endorsements },
												{ label: 'Pilot Rating Required', value: aircraftForHeader.pilotRating?.name ?? aircraftForHeader.pilotRatingRequired ?? aircraftForHeader.pilotRating },
												{ label: 'Instructor Rating Required', value: aircraftForHeader.instructorRating?.name ?? aircraftForHeader.instructorRatingRequired ?? aircraftForHeader.instructorRating }
											].map(row => {
												const display = row.value == null || row.value === '' || row.value === false
													? '—'
													: (row.value === true ? 'Yes' : String(row.value));
												return (
													<li key={row.label} className="px-3 py-2 grid grid-cols-12 gap-2 items-center text-xs">
														<span className="col-span-5 text-default-600">{row.label}</span>
														<span className="col-span-7 text-default-800">{display}</span>
													</li>
												);
											})}
										</ul>
									</AccordionItem>
								</Accordion>
								</section>
							)}

							{/* Meters Values table — Meters / Start / End / Total.
							    Legacy parity `calendar.event-checkin.js:127-173`.
							    Start is OLD reading (readonly); End is editable +
							    auto-formatted to 2 decimals on blur; Total is
							    computed live. Hobbs End cascades into the custom
							    resource counters below whose org meter is `hobbs`.
							    When the event is out-of-sequence, Start displays the
							    LIVE `c.value` (Rem38 M8), with the previous reading
							    surfaced as a `Previous:` hint. */}
							{metersRows.length > 0 && (
								<section className="border border-default-200 rounded-md overflow-hidden">
									{/* Rem21 Sub 4 — switched semantic <header> → <div> to
									    sidestep Bootstrap legacy CSS that injects padding /
									    margin on the `<header>` element and was breaking
									    the grid alignment with the row layout below. */}
									<div className="px-3 py-2 bg-default-50 border-b border-default-200 grid grid-cols-12 gap-2 text-xs font-semibold uppercase tracking-wide text-default-500">
										<span className="col-span-4">Meters</span>
										<span className="col-span-3">Start</span>
										<span className="col-span-3">End</span>
										<span className="col-span-2 text-right">Total</span>
									</div>
									<ul className="divide-y divide-default-100">
										{metersRows.map(row => {
											const negative = row.total != null && row.total < 0;
											return (
												<li
													key={row.id}
													className="px-3 py-2 grid grid-cols-12 gap-2 items-center"
												>
													<span className="col-span-4 font-medium text-default-800">{row.name}</span>
													<span className="col-span-3 font-mono text-default-700">{row.start}</span>
													<span className="col-span-3 flex flex-col gap-0.5">
														{/* Rem38 M1 — `type="text"` + `inputMode="decimal"`
														    + `lang="en"` instead of `type="number"`: skips
														    the browser's locale-based number formatting
														    (UA browsers render `61,50`) so the value shown
														    keeps the dot separator and stays consistent
														    with the Start column. `handleMeterEndChange`
														    normalizes any typed comma to a dot. */}
														<Input
															size="sm"
															aria-label={`${row.name} End`}
															type="text"
															inputMode="decimal"
															lang="en"
															value={row.endText}
															onValueChange={v => handleMeterEndChange(row.id, v)}
															onBlur={() => handleMeterEndBlur(row.id)}
														/>
														{row.previousHint && (
															<span className="text-[10px] text-default-400 leading-none">
																Previous: {row.previousHint}
															</span>
														)}
													</span>
													<span className={`col-span-2 text-right font-mono ${negative ? 'text-danger' : 'text-default-700'}`}>
														{row.total == null
															? '—'
															: (negative ? <span className="inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" />{row.total.toFixed(2)}</span> : row.total.toFixed(2))}
													</span>
												</li>
											);
										})}
									</ul>
								</section>
							)}

							{/* Custom resource counters table (Phase 2.1 Task:1, restored).
							    One row per non-aircraft/sim resource: name + a single
							    editable diff input (2-dp blur) + the computed New Value
							    (`counterValue + diffCounter`). Hobbs End cascades into the
							    diff for hobbs-sourced resources. Legacy parity:
							    `calendar.event-checkin.js:415-446`. */}
							{resourceRows.length > 0 && (
								<section className="border border-default-200 rounded-md overflow-hidden">
									<div className="px-3 py-2 bg-default-50 border-b border-default-200 grid grid-cols-12 gap-2 text-xs font-semibold uppercase tracking-wide text-default-500">
										<span className="col-span-5">Resource</span>
										<span className="col-span-4">Diff</span>
										<span className="col-span-3 text-right">New Value</span>
									</div>
									<ul className="divide-y divide-default-100">
										{resourceRows.map(row => (
											<li
												key={`res-${row.id}`}
												className="px-3 py-2 grid grid-cols-12 gap-2 items-center"
											>
												<span className="col-span-5 font-medium text-default-800">
													{row.name}
													{row.type ? (
														<span className="ml-1 text-default-400 font-normal">{row.type}</span>
													) : null}
												</span>
												<span className="col-span-4">
													<Input
														size="sm"
														aria-label={`${row.name} counter difference`}
														type="text"
														inputMode="decimal"
														lang="en"
														value={row.diffCounter}
														onValueChange={v => handleResourceDiffChange(row.id, v)}
														onBlur={() => handleResourceDiffBlur(row.id)}
													/>
												</span>
												<span className="col-span-3 text-right font-mono text-default-700">
													{row.newCounterValue === '' ? '—' : row.newCounterValue}
												</span>
											</li>
										))}
									</ul>
								</section>
							)}

							{bootstrap && metersRows.length === 0 && (
								<p className="text-default-400 italic">
									No counters configured for this resource.
								</p>
							)}

							{/* Event Fields — per-org custom fields editable on
							    Check In. Legacy parity `calendar.event-checkin.js:174-260`.
							    Required fields are validated in `handleSubmit`.
							    Sub 11b: source is `mergedCustomFields` — every
							    org-level definition rendered (not just the ones with
							    pre-set values). */}
							{mergedCustomFields.length > 0 && (
								<section className="border border-default-200 rounded-md overflow-hidden">
									<div className="px-3 py-2 bg-default-50 border-b border-default-200">
										<span className="text-xs font-semibold uppercase tracking-wide text-default-500">Event Fields</span>
									</div>
									<ul className="divide-y divide-default-100">
										{mergedCustomFields.map(cf => {
											const typeId = cf?.typeId ?? cf?.id;
											const key = String(typeId);
											const type = String(cf?.type || 'text').toLowerCase();
											const isDate = type === 'date';
											const inputType = type === 'number' || type === 'numeric'
												? 'number'
												: 'text';
											const required = cf?.required === true || cf?.required === '1' || cf?.required === 1;
											// Rem22 Sub 7 — per-field invalid state.
											const hasError = !!customFieldErrors[key];
											const clearError = () => {
												if (hasError) {
													setCustomFieldErrors(prev => {
														const next = { ...prev };
														delete next[key];
														return next;
													});
												}
											};
											return (
												<li key={key} className="px-3 py-2 grid grid-cols-12 gap-2 items-center">
													<span className="col-span-5 font-medium text-default-800">
														{cf?.name || cf?.label || `Field #${typeId}`}
														{required && <span className="text-danger ml-1">*</span>}
													</span>
													<span className="col-span-7 flex flex-col gap-0.5">
														{/* Rem21 Sub 6 — `type === 'date'` renders the
														    project AppDatePicker (not the native
														    browser datepicker which mismatched the
														    other date inputs in the modal).
														    Rem22 Sub 7 — wrap in red-border div +
														    helper text when the field failed required
														    validation; user typing clears the error. */}
														{isDate ? (
															<div
																style={hasError ? { boxShadow: '0 0 0 1px hsl(var(--heroui-danger))', borderRadius: '0.5rem' } : undefined}
															>
																<AppDatePicker
																	aria-label={cf?.name || `custom field ${typeId}`}
																	granularity="day"
																	value={customFieldValues[key] || null}
																	onChange={cdt => {
																		clearError();
																		setCustomFieldValues(prev => ({
																			...prev,
																			[key]: cdt ? calendarDateToIso(cdt) : ''
																		}));
																	}}
																/>
															</div>
														) : (
															<Input
																size="sm"
																aria-label={cf?.name || `custom field ${typeId}`}
																type={inputType}
																value={String(customFieldValues[key] ?? '')}
																isInvalid={hasError}
																errorMessage={hasError ? 'This field is required.' : undefined}
																onValueChange={v => {
																	clearError();
																	setCustomFieldValues(prev => ({
																		...prev,
																		[key]: v
																	}));
																}}
															/>
														)}
														{hasError && isDate && (
															<span className="text-xs text-danger">This field is required.</span>
														)}
													</span>
												</li>
											);
										})}
									</ul>
								</section>
							)}

							{/* Comments — pre-filled from event.comments. The wire
							    field name is SINGULAR `comment` per backend DTO
							    (`CalendarCheckInManager.php:141-143`); the adapter
							    renames on submit. */}
							<section className="space-y-1">
								<label htmlFor="check-in-comment" className="block text-xs font-medium text-default-600">
									Comments
								</label>
								<textarea
									id="check-in-comment"
									className="w-full border border-default-200 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
									rows={3}
									value={commentText}
									onChange={e => setCommentText(e.target.value)}
								/>
							</section>

							{/* Discrepancies — shared canonical `DiscrepanciesSection`
							    (epic e5cea696). Identical filter tabs / columns / inline
							    edit / div-grid layout as the View Resource surface
							    (ResourcePanel). `localDiscrepancies` is sourced solely
							    from `getAircraftDiscrepancies` (the clobber useEffect was
							    removed). Add button gates on `canCreate`. */}
							<section>
								<div className="flex items-center justify-between mb-2">
									<p className="text-xs font-semibold text-default-500 uppercase tracking-wide">
										Discrepancies
									</p>
									{discrepancyPerms.canCreate && (
										<Button
											size="sm"
											color="success"
											variant="flat"
											onPress={handleAddDiscrepancyClick}
										>
											+ Add
										</Button>
									)}
								</div>
								<DiscrepanciesSection
									discrepancies={localDiscrepancies}
									userSettings={userSettings}
									onDiscrepancyClick={handleDiscrepancyRowClick}
									canViewDiscrepancies={discrepancyPerms.canView}
									canChangeDiscrepancyStatus={discrepancyPerms.canChangeStatus}
									canManageDiscrepancies={discrepancyPerms.canEdit}
									orgUnique={orgUnique}
									onDiscrepancyUpdated={refreshDiscrepancies}
									mechanics={mechanics}
								/>
							</section>

							{/* Track Flight readonly summary + Actual Arrival editable.
							    Legacy parity: `calendar.event-checkin.js:44-95, 275-312`.
							    Visible only when org enables Track Flight AND event
							    isn't maintenance. Actual Arrival is required on submit. */}
							{showTrackFlight && trackFlightReadonly && (
								<section className="border-t border-default-200 pt-4 space-y-3">
									<h3 className="text-sm font-semibold text-default-700">Track Flight</h3>
									<p className="text-xs text-default-500">
										All times in event location timezone ({tz}). Departure,
										Flight Time, and Expected Arrival were recorded at
										Check Out and cannot be edited here.
									</p>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
										<div className="flex flex-col gap-1">
											<label className="text-xs font-medium text-default-600">Departure</label>
											<span className="text-sm font-mono text-default-700 px-2 py-1 rounded bg-default-50 border border-default-200">
												{trackFlightReadonly.departureText}
											</span>
										</div>
										<div className="flex flex-col gap-1">
											<label className="text-xs font-medium text-default-600">Flight Time</label>
											<span className="text-sm font-mono text-default-700 px-2 py-1 rounded bg-default-50 border border-default-200">
												{trackFlightReadonly.flightTimeText}
											</span>
										</div>
										<div className="flex flex-col gap-1">
											<label className="text-xs font-medium text-default-600">Expected Arrival</label>
											<span className="text-sm font-mono text-default-700 px-2 py-1 rounded bg-default-50 border border-default-200">
												{trackFlightReadonly.expectedArrivalText}
											</span>
										</div>
									</div>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
										<div className="flex flex-col gap-1">
											<label className="text-xs font-medium text-default-600">
												Actual Arrival <span className="text-danger">*</span>
											</label>
											<AppDatePicker
												aria-label="Actual Arrival"
												granularity="minute"
												hourCycle="h23"
												hideTimeZone
												isRequired
												value={actualArrivalCdt}
												onChange={setActualArrivalCdt}
											/>
										</div>
									</div>
								</section>
							)}
						</div>
					)}
				</motion.div>
			</ModalBody>

			<ModalFooter className="justify-between gap-2 flex-wrap border-t border-default-200 pt-3">
				<Button size="sm" variant="bordered" onPress={onBack}>
					Cancel
				</Button>
				<div className="flex gap-2 flex-wrap">
					{/*
						Submit is enabled once the bootstrap loads cleanly. Sub 12
						will replace `handleSubmit`'s placeholder with the real
						submit; in the meantime this enables the click path so
						the Actual Arrival required-field guard can be smoke-
						tested end-to-end.
					*/}
					<Button
						size="sm"
						color="success"
						isLoading={submitting}
						isDisabled={loading || !!criticalError}
						onPress={handleSubmit}
					>
						Check In
					</Button>
					<Button size="sm" variant="bordered" onPress={onClose}>
						Close
					</Button>
				</div>
			</ModalFooter>
		</>
	);
}

export default CheckInScreen;
